import { useState } from 'react'
import { useNetworkStatus } from '@/api'
import { isApiError } from '@/api'
import { useFormat, useI18n } from '@/i18n'
import { env } from '@/lib/env'
import { formatPaise } from '@/lib/money'
import { istDateKey } from '@/lib/ist'
import { useDeviceInfo } from '@/offline'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChoiceGroup,
  Field,
  IntegerInput,
  KgInput,
  PhotoCapture,
  useToast,
} from '@/ui'
import type { PackRunView, ProcessState } from '@/features/process/projection'
import { Line, YesNo } from '@/features/process/components/bits'
import { useNow } from '@/features/process/useProcess'
import { startPackRun } from '../../api'

const REFERENCE_G = 500

/**
 * F7.2 — Start the pack run at the line: empty and clean (allergen clean when the crop changed),
 * check the scale with the 500 g weight, confirm the label roll. The server then fixes the pack
 * lot's code, dates and QR link — so this one step needs network.
 */
export function StartSection({ state, run, blocked }: { state: ProcessState; run: PackRunView; blocked: boolean }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const now = useNow()
  const { online } = useNetworkStatus()
  const info = useDeviceInfo()
  const demo = env.isDev && !!info.registration?.team.id.startsWith('dev-')
  const today = istDateKey(now)

  const [allergen, setAllergen] = useState<boolean | null>(null)
  const [cleanout, setCleanout] = useState<boolean | null>(null)
  const [photo, setPhoto] = useState<string | null>(null)
  const [leftover, setLeftover] = useState<number | null>(null)
  const [scaleId, setScaleId] = useState<string | null>(null)
  const [reading, setReading] = useState<number | null>(null)
  const [labelId, setLabelId] = useState<string | null>(run.label_versions.length === 1 ? run.label_versions[0]!.id : null)
  const [labelOk, setLabelOk] = useState<boolean | null>(null)
  const [saving, setSaving] = useState(false)

  const licenceOk = !!run.premises_licence_no && !!run.premises_licence_valid_to && run.premises_licence_valid_to.slice(0, 10) >= today
  const scaleOverdue = (s: (typeof state.pack.scales)[number]) =>
    (!!s.calibration_due_at && s.calibration_due_at.slice(0, 10) < today) ||
    (!!s.stamp_due_at && s.stamp_due_at.slice(0, 10) < today)
  const scale = state.pack.scales.find((s) => s.id === scaleId)
  const scaleOk = !!scale && reading !== null && Math.abs(reading - REFERENCE_G) <= scale.check_tolerance_g
  const label = run.label_versions.find((l) => l.id === labelId)
  const ready =
    !blocked &&
    licenceOk &&
    (!run.allergen_clean_required || allergen === true) &&
    cleanout === true &&
    !!photo &&
    leftover !== null &&
    scaleOk &&
    !!label &&
    labelOk === true &&
    (online || demo)

  const start = async () => {
    if (!ready || !scale || !label || !photo || leftover === null || reading === null) return
    setSaving(true)
    try {
      const res = await startPackRun(run, {
        cleanout_photo_id: photo,
        allergen_clean_confirmed: allergen === true,
        leftover_g: leftover,
        scale_check: { equipment_id: scale.id, reference_g: REFERENCE_G, reading_g: reading },
        label_version_id: label.id,
      })
      toast.success(t('pack.start.done', { code: res.pack_lot.code }))
    } catch (e) {
      const msg = isApiError(e) && e.kind === 'network' ? t('pack.start.needsNetwork') : e instanceof Error ? e.message : undefined
      toast.error(t('pack.start.failed'), msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="py-1">
          <Line
            label={t('pack.start.licence')}
            value={
              run.premises_licence_no
                ? `${run.premises_licence_no}${run.premises_licence_valid_to ? ` · ${fmt.date(run.premises_licence_valid_to)}` : ''}`
                : '—'
            }
            tone={licenceOk ? 'ok' : 'danger'}
          />
          <Line
            label={t('pack.start.lastCrop')}
            value={run.previous_crop_name ?? t('pack.start.lastCropUnknown')}
          />
        </CardBody>
      </Card>
      {!licenceOk && <Banner tone="error">{t('pack.start.licenceBad')}</Banner>}

      {run.allergen_clean_required && (
        <Field label={<BilingualLabel k="pack.start.allergen" />} hint={t('pack.start.allergenHint')} required>
          <YesNo value={allergen} onChange={setAllergen} />
        </Field>
      )}
      <Field label={<BilingualLabel k="pack.start.cleanout" />} required>
        <YesNo value={cleanout} onChange={setCleanout} />
      </Field>
      <Field label={<BilingualLabel k="pack.start.photo" />} required>
        <PhotoCapture value={photo} onChange={setPhoto} />
      </Field>
      <Field label={<BilingualLabel k="pack.start.leftover" />} hint={t('pack.start.leftoverHint')} required>
        <KgInput value={leftover} onChange={setLeftover} />
      </Field>

      <Card>
        <CardHeader eyebrow={<BilingualLabel k="pack.start.scaleTitle" />} />
        <CardBody className="flex flex-col gap-3">
          <ChoiceGroup
            layout="stack"
            options={state.pack.scales.map((s) => ({
              value: s.id,
              label: scaleOverdue(s) ? `${s.name} · ${t('pack.start.scaleOverdue')}` : s.name,
              disabled: scaleOverdue(s),
            }))}
            value={scaleId}
            onChange={setScaleId}
          />
          <Field
            label={<BilingualLabel k="pack.start.checkReading" />}
            hint={t('pack.start.checkHint', { ref: REFERENCE_G, tol: scale?.check_tolerance_g ?? 0 })}
            error={scale && reading !== null && !scaleOk ? t('pack.start.checkOff') : undefined}
            required
          >
            <IntegerInput value={reading} onChange={setReading} maxDigits={4} />
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader eyebrow={<BilingualLabel k="pack.start.label" />} />
        <CardBody className="flex flex-col gap-3">
          {run.label_versions.length === 0 ? (
            <Banner tone="error">{t('pack.start.noLabel')}</Banner>
          ) : (
            <ChoiceGroup
              layout="stack"
              options={run.label_versions.map((l) => ({ value: l.id, label: l.code }))}
              value={labelId}
              onChange={(v) => {
                setLabelId(v)
                setLabelOk(null)
              }}
            />
          )}
          {label && (
            <div>
              <Line label={t('pack.label.mrp')} value={label.mrp_paise != null ? formatPaise(label.mrp_paise) : '—'} />
              <Line label={t('pack.label.net')} value={run.sku.net_g >= 1000 ? fmt.weight(run.sku.net_g) : `${run.sku.net_g} g`} />
              <Line label={t('pack.label.licence')} value={label.fssai_licence_no ?? run.premises_licence_no ?? '—'} />
              <div className="py-2 text-[14px]">
                <div className="text-muted">{t('pack.label.allergen')}</div>
                <div className="font-bold">{label.allergen_statement ?? '—'}</div>
              </div>
              {label.claim_text && (
                <div className="py-2 text-[14px]">
                  <div className="text-muted">{t('pack.label.claim')}</div>
                  <div>{label.claim_text}</div>
                </div>
              )}
              <Field label={<BilingualLabel k="pack.start.labelMatches" />} required>
                <YesNo value={labelOk} onChange={setLabelOk} />
              </Field>
              {labelOk === false && <Banner tone="error">{t('pack.start.labelWrong')}</Banner>}
            </div>
          )}
        </CardBody>
      </Card>

      {!online && !demo && <Banner tone="warn">{t('pack.start.needsNetwork')}</Banner>}
      <Button size="xl" fullWidth disabled={!ready} loading={saving} onClick={() => void start()}>
        <BilingualLabel k="pack.start.button" />
      </Button>
    </div>
  )
}
